import React from 'react';
import { Hotel } from '../../types/Quotation';

interface HotelCardProps {
  hotel: Hotel & { day: number };
}

const HotelCard: React.FC<HotelCardProps> = ({ hotel }) => {
  const renderStars = (rating: string) => {
    const stars = parseInt(rating) || 0;
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  };

  const getAmenityIcon = (amenity: string) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi') || amenityLower.includes('internet')) return '📶';
    if (amenityLower.includes('pool') || amenityLower.includes('swimming')) return '🏊';
    if (amenityLower.includes('gym') || amenityLower.includes('fitness')) return '💪';
    if (amenityLower.includes('spa') || amenityLower.includes('wellness')) return '🧘';
    if (amenityLower.includes('restaurant') || amenityLower.includes('dining')) return '🍽️';
    if (amenityLower.includes('parking')) return '🅿️';
    if (amenityLower.includes('airport') || amenityLower.includes('shuttle')) return '🚌';
    if (amenityLower.includes('concierge') || amenityLower.includes('service')) return '🛎️';
    if (amenityLower.includes('bar') || amenityLower.includes('lounge')) return '🍸';
    if (amenityLower.includes('business') || amenityLower.includes('meeting')) return '💼';
    return '✨';
  };

  return (
    <div style={{
      border: '1px solid #e1e5e9',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '16px'
    }}>
      {/* Hotel Images */}
      <div style={{ flex: '0 0 120px' }}>
        {/* Main Thumbnail */}
        <div style={{
          width: '100%',
          height: '80px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6c757d',
          fontSize: '24px',
          marginBottom: '8px'
        }}>
          {hotel.thumbnail ? (
            <img
              src={hotel.thumbnail}
              alt={hotel.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          ) : (
            '🏨'
          )}
        </div>

        {/* Image Gallery */}
        {(() => {
          const galleryImages: string[] = [];
          
          if (hotel.roomVariant?.images && Array.isArray(hotel.roomVariant.images)) {
            galleryImages.push(...hotel.roomVariant.images);
          }
          
          if (hotel.roomVariant?.gallery && Array.isArray(hotel.roomVariant.gallery)) {
            galleryImages.push(...hotel.roomVariant.gallery);
          }
          const uniqueImages = Array.from(new Set(galleryImages.filter(img => img && img.trim())));
          
          if (uniqueImages.length === 0) return null;
          
          return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {uniqueImages.slice(0, 3).map((imageUrl: string, index: number) => (
              <div
                key={index}
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={imageUrl}
                  alt={`${hotel.name} gallery ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#e9ecef',
                  color: '#6c757d',
                  fontSize: '12px'
                }}>
                  🖼️
                </div>
              </div>
            ))}
              {uniqueImages.length > 3 && (
                <div style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  +{uniqueImages.length - 3}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Hotel Details */}
      <div style={{ flex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {hotel.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <span style={{ color: '#f39c12', fontSize: '14px' }}>
                {renderStars(hotel.starRating)}
              </span>
              <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                {hotel.starRating} stars
              </span>
            </div>
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
            📍 {hotel.address}
          </span>
        </div>

        {/* Room Details */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
          {hotel.roomType && (
            <div style={{ fontSize: '12px', color: '#34495e' }}>
              <strong>Room:</strong> {hotel.roomType}
            </div>
          )}
        </div>

        {/* Amenities */}
        {hotel.amenities && hotel.amenities.length > 0 && (
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>
              <strong>Amenities:</strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {hotel.amenities.slice(0, 6).map((amenity, index) => (
                <span key={index} style={{ 
                  fontSize: '12px', 
                  color: '#34495e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px'
                }}>
                  {getAmenityIcon(amenity)} {amenity}
                </span>
              ))}
              {hotel.amenities.length > 6 && (
                <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  +{hotel.amenities.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Check-in/Check-out Times */}
        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
          <div>Check-in: {hotel.checkin_time}</div>
          <div>Check-out: {hotel.checkout_time}</div>
        </div>
      </div>
    </div>
  );
};

export default HotelCard;